// Pure geometry helpers used by the viewport-driven list and the
// colored-pin percentile scale. No side effects, no Leaflet imports.

import type { Bbox } from "./types";

export interface LatLng {
  lat: number;
  lng: number;
}

export function inBbox(point: LatLng, bbox: Bbox): boolean {
  return (
    point.lat >= bbox.south &&
    point.lat <= bbox.north &&
    point.lng >= bbox.west &&
    point.lng <= bbox.east
  );
}

/**
 * Linear-interpolated percentile. Accepts any unsorted iterable, returns
 * NaN for empty input, clamps p to [0, 1].
 *
 * Used by the map to pick pin colors: the Nth percentile of visible
 * prices draws the "cheap" threshold, (1-N)th the "expensive" threshold.
 */
export function percentile(values: readonly number[], p: number): number {
  if (values.length === 0) return NaN;
  const sorted = [...values].sort((a, b) => a - b);
  if (p <= 0) return sorted[0];
  if (p >= 1) return sorted[sorted.length - 1];
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

const EARTH_R_KM = 6371;

/**
 * Great-circle distance between two points (haversine). Return value
 * is kilometres. Good to about 0.5% accuracy — fine for a "cheapest near
 * me" list, not for surveying.
 */
export function distanceKm(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_R_KM * Math.asin(Math.sqrt(h));
}

/**
 * Smallest axis-aligned bbox covering every point. Used by the region
 * selector to frame a set of stations on the map. Empty input → world bbox
 * (so Leaflet fitBounds doesn't crash — caller can guard if it prefers).
 */
export function fitsBboxOf(points: readonly LatLng[]): Bbox {
  if (points.length === 0) {
    return { west: -180, south: -90, east: 180, north: 90 };
  }
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const p of points) {
    if (p.lng < west) west = p.lng;
    if (p.lng > east) east = p.lng;
    if (p.lat < south) south = p.lat;
    if (p.lat > north) north = p.lat;
  }
  return { west, south, east, north };
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}
